import {
  HolisticProjectStages,
  HolisticProjectTypes,
} from '@aims/shared/holistic-projects/constants';
import useManyRemoteM1 from '@aims/shared/shared/use-many-remote-m1';
import { EyeOutlined } from '@ant-design/icons';
import { Button, Space, Table, Tooltip } from 'antd';
import Column from 'antd/lib/table/Column';
import React, { useCallback, useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import settings from '../../settings';
import {
  allHolisticProjectsForAdminQuery,
  extractHolisticProjectsForAdmin,
} from './constants';
import FilterHolisticProjects from './FilterHolisticProjects';
import {
  default as getProjectStatus,
  default as ProjectStatusColumn,
} from './get-project-status';
import getChecklist from './get-checklist';
import { getPgDisplayName } from '@aims/shared/people-groups/utils';
import { displayMoney, getNumberValue } from '@aims/shared/shared/utils';

function HolisticProjectsList({ filters: parentFilters }) {
  const [sortedBy, setSortedBy] = useState('updatedAt');
  const [sortOrder, setSortOrder] = useState('DESC');
  const sortBy = useRef([
    { key: sortedBy, order: sortOrder },
    { key: '_score', order: 'DESC' },
  ]);

  const filters = useRef({
    humanReadableId: -1,
    ...parentFilters,
  });

  const {
    error,
    loading,
    data: holisticProjects,
    search: holisticProjectSearch,
    hasNextPage,
    next,
    refetch,
    reset,
  } = useManyRemoteM1({
    query: allHolisticProjectsForAdminQuery,
    extract: extractHolisticProjectsForAdmin,
    first: settings.querySize,
    filters: filters.current,
    sortBy: sortBy.current,
    fetchPolicy: 'network-only',
    queryId: 'holisticProjectsList',
  });

  const locale = useSelector((store) => store.locale, shallowEqual);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: settings.pageSize,
  });
  const handleTableChange = useCallback((params) => {
    setPagination({
      ...params.pagination,
    });
  }, []);

  return (
    <>
      <FilterHolisticProjects
        filters={filters}
        refetch={refetch}
        reportSearch={holisticProjectSearch}
        loading={loading}
        sortBy={sortBy}
        type={'holisticProjectList'}
        projects={holisticProjects}
      />
      <Table
        dataSource={holisticProjects}
        loading={!holisticProjects.length && loading}
        pagination={{ ...pagination, total: holisticProjects.length }}
        onChange={handleTableChange}
        style={{ width: '100%' }}
        rowKey="_id"
        scroll={{ x: 1600 }}
      >
        <Column
          title="Project #"
          dataIndex="projectNumber"
          key="projectNumber"
          width="8em"
          render={(_, record) =>
            record.projectNumber || record.projectNo || '-'
          }
        />
        <Column
          title="Region"
          dataIndex="regionCode"
          key="regionCode"
          width={120}
          render={(_, record) => record.region || record.regionCode || ''}
        />
        <Column
          title="Project Name"
          width={200}
          dataIndex="projectName"
          key="projectName"
        />
        <Column
          title="Stage"
          dataIndex="stage"
          key="stage"
          render={(text, record) => {
            return HolisticProjectStages[record.stage]?.label;
          }}
        />
        <Column
          title="Status"
          dataIndex="status"
          key="status"
          width={300}
          render={(text, record) => {
            return getProjectStatus(record);
          }}
        />
        <Column
          title="Checklist"
          dataIndex="checklist"
          key="checklist"
          width={300}
          render={(text, record) => {
            return getChecklist(record);
          }}
        />

        <Column
          title="Loan Amount"
          dataIndex="loanTotalX4"
          render={(text, record) => {
            return displayMoney(
              getNumberValue(record.loanTotalX4 || 0) / 10000,
            );
          }}
        />

        <Column
          title="Coordinator"
          dataIndex="regionalCoordId"
          width={200}
          render={(text, record) => {
            return (
              record.regionalCoordinator && (
                <Link
                  to={`/contact/view/${record.regionalCoordinator._id}/dashboard`}
                >
                  {record.regionalCoordinator.fullName}
                </Link>
              )
            );
          }}
        />
        <Column
          title="People Groups"
          dataIndex="peopleGroupIds"
          width={200}
          render={(text, record) => {
            return (
              record.peopleGroups &&
              record.peopleGroups.map((pg) => (
                <Link
                  key={pg._id}
                  style={{ marginRight: 8 }}
                  to={`/people-group/view/${pg._id}/info`}
                >
                  {getPgDisplayName(pg)}
                </Link>
              ))
            );
          }}
        />
        <Column
          title="Project Type"
          dataIndex="typeOfProject"
          key="typeOfProject"
          render={(text, record) => {
            return HolisticProjectTypes[record.typeOfProject]?.label;
          }}
        />
        <Column
          title="Created"
          dataIndex="createdAt"
          render={(text, record) => {
            return new Intl.DateTimeFormat(locale, {
              dateStyle: 'short',
            }).format(new Date(record.createdAt));
          }}
        />
        <Column
          title="Action"
          key="action"
          render={(text, record) => (
            <Space size="middle">
              <Tooltip title="View">
                <Link
                  to={`/holistic-project/view/${record._id}/details?from=${location.pathname}`}
                >
                  <Button icon={<EyeOutlined />} />
                </Link>
              </Tooltip>
            </Space>
          )}
        />
      </Table>
      <div>{hasNextPage && <Button onClick={next}>Load More</Button>}</div>
    </>
  );
}

export default HolisticProjectsList;
